
/**
 * Simplified i18n implementation with full English-only support
 */

// Define how translations are structured
type TranslationsSource = {
  [key: string]: string | TranslationsSource;
};

// Flattens nested translation objects into a flat dictionary
const flattenTranslations = (
  obj: TranslationsSource,
  prefix: string = ''
): Record<string, string> => {
  return Object.keys(obj).reduce((acc: Record<string, string>, k: string) => {
    const pre = prefix.length ? `${prefix}.` : '';
    if (
      typeof obj[k] === 'object' &&
      obj[k] !== null &&
      !Array.isArray(obj[k])
    ) {
      Object.assign(acc, flattenTranslations(obj[k] as TranslationsSource, `${pre}${k}`));
    } else {
      acc[`${pre}${k}`] = obj[k] as string;
    }
    return acc;
  }, {});
};

/**
 * Loads all translations for a specific language
 * @param language The language code to load
 * @returns A promise that resolves to a flattened record of translations
 */
export const loadLanguage = async (
  language: string
): Promise<Record<string, string>> => {
  try {
    console.log(`Loading language: ${language}`);
    let translations: Record<string, string> = {};
    
    // Load base translations first
    try {
      // Only English is supported now
      console.log('Importing English translations module');
      const module = await import('./languages/en.ts');
      const languageData = module.default || {};
      const flattened = flattenTranslations(languageData);
      
      console.log(`Loaded English translations: ${Object.keys(flattened).length} keys`);
      
      // Validate critical match page keys
      const matchKeys = Object.keys(flattened).filter(key => key.startsWith('match.'));
      console.log(`Found ${matchKeys.length} match-related translation keys`);
      
      translations = { ...translations, ...flattened };
    } catch (error) {
      console.error(`Error loading English translations:`, error);
    }
    
    // Return the combined translations
    return translations;
  } catch (error) {
    console.error(`Failed to load language '${language}':`, error);
    // Return an empty object if the language fails to load
    return {};
  }
};
